import axios from 'axios';
import { APP_NAME, APP_VERSION, clientLog, correlationHeaders } from '../observability';

// Get baseUrl from environment variable, fallback to production URL
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api-pegasus.flixstudio.io/api/v1';

function withCorrelation(headers = {}) {
  const ids = correlationHeaders();
  return {
    headers: {
      ...headers,
      'x-request-id': ids['x-request-id'],
      'x-correlation-id': ids['x-correlation-id'],
      'x-app-version': APP_VERSION,
      'x-app-name': APP_NAME,
    },
    requestId: ids.requestId,
    correlationId: ids.correlationId,
  };
}

function logApiFailure(err, meta) {
  clientLog.apiFailed(err?.message || 'API request failed', {
    method: meta.method,
    url: meta.url,
    statusCode: err?.response?.status,
    requestId: meta.requestId,
    correlationId: meta.correlationId,
    error: {
      name: err?.name,
      message: err?.message,
      stack: err?.stack,
    },
  });
}

function applyObservability(client) {
  client.interceptors.request.use((config) => {
    const corr = withCorrelation(config.headers || {});
    config.headers = corr.headers;
    config.requestId = corr.requestId;
    config.correlationId = corr.correlationId;
    return config;
  });
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error?.config || {};
      logApiFailure(error, {
        method: config.method,
        url: config.url,
        requestId: config.requestId,
        correlationId: config.correlationId,
      });
      return Promise.reject(error);
    }
  );
  return client;
}

axios.interceptors.request.use((config) => {
  const corr = withCorrelation(config.headers || {});
  config.headers = corr.headers;
  config.requestId = corr.requestId;
  config.correlationId = corr.correlationId;
  return config;
});
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error?.config || {};
    logApiFailure(error, {
      method: config.method,
      url: config.url,
      requestId: config.requestId,
      correlationId: config.correlationId,
    });
    return Promise.reject(error);
  }
);

export const request_v2 = async ({
  method,
  params,
  url,
  body = null,
  transformRequest = null,
  transformResponse = null,
  timeout = 0,
  responseType = 'json',
  onUploadProgress = null,
  onDownloadProgress = null,
}) => {
  const corr = withCorrelation({
    authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });
  const options = {
    baseURL: baseUrl,
    url: url,
    method: method,
    transformRequest: transformRequest,
    transformResponse: transformResponse,
    headers: corr.headers,
    params: params,
    data: !!body && JSON.stringify(body),
    timeout: timeout,
    withCredentials: false,
    responseType: responseType,
    responseEncoding: 'utf8',
    onUploadProgress: onUploadProgress,
    onDownloadProgress: onDownloadProgress,
    validateStatus: function (status) {
      return status >= 200 && status < 300; // default
    },
    maxRedirects: 5,
    decompress: true,
    requestId: corr.requestId,
    correlationId: corr.correlationId,
  };
  try {
    const { data, status, statusText, headers } = await axios.request(options);
    return { data: JSON.parse(data), status, statusText, headers };
  } catch (err) {
    logApiFailure(err, {
      method,
      url,
      requestId: corr.requestId,
      correlationId: corr.correlationId,
    });
    throw err;
  }
};

export const GetRequest = async options => {
  const client = applyObservability(axios.create({
    baseURL: baseUrl,
    headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
    params: options.QueryParams,
  }));
  return client.request(options);
};

export const request = async options => {
  const client = applyObservability(axios.create({
    baseURL: baseUrl,
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  }));
  return client.request(options);
};

export const requestXlsxFileDownload = async options => {
  const client = applyObservability(axios.create({
    baseURL: baseUrl,
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
      'Access-Control-Expose-Headers': 'Content-Disposition',
    },
    responseType: 'blob',
  }));
  return client.request(options);
};

export const upload = async (header, options) => {
  const client = applyObservability(axios.create({ headers: header }));
  return client.request(options);
};
